import { FC, memo, useCallback, useRef, useState } from 'react'
import { AnnouncementListItem } from './components/announcementListItem/announcementListItem'
import { useAnnouncementList } from './useAnnouncementList'
import { useValueChangeEffect } from '@crew/hooks'
import { useAppSelector } from 'states/hooks'
import { useEffectOnce } from '@dx-system/react-use'

export const AnnouncementList: FC = memo(() => {
  const {
    announcementInView,
    searchAnnouncements,
    resetAnnouncements,
    displayItems,
    unreadOnly,
  } = useAnnouncementList()

  // ログインユーザー
  const loggedInUser = useAppSelector((state) => state.app.loggedInUser)

  const [selectedAnnouncementId, setSelectedAnnouncementId] = useState<
    string | undefined
  >(undefined)

  // 画面遷移前のスクロール位置
  const scrollToAnnouncementId = useAppSelector(
    (state) => state.announcement.scrollToAnnouncementId
  )

  // メッセージ表示領域のref
  const itemsScrollableDivRef = useRef<HTMLDivElement>(null)

  // 他画面への遷移から戻った際に、スクロール位置を元の位置に戻すための処理
  useEffectOnce(() => {
    // scrollToAnnouncementIdがなければ処理しない
    if (scrollToAnnouncementId === undefined) {
      return
    }

    // スクロール対象のDOM要素
    const displayTargetElement = itemsScrollableDivRef.current?.querySelector(
      `#announcement_${scrollToAnnouncementId}`
    )

    // スクロール対象のメッセージを表示中アイテムの中から取得する
    const item = displayItems.find(
      (item) => item.announcement.id === scrollToAnnouncementId
    )

    // スクロール対象のメッセージが表示アイテムの中にあり、スクロール対象のDOM要素がある場合のみ処理を行う
    if (item && displayTargetElement) {
      // 表示対象のDOM要素を表示領域内にスクロールする
      displayTargetElement.scrollIntoView({
        behavior: 'auto',
        block: 'center',
      })
    }
  })

  // ログインユーザーが変わったら初期ロードを行う
  useValueChangeEffect(
    () => {
      searchAnnouncements()
    },
    [loggedInUser, searchAnnouncements],
    loggedInUser
  )

  // 「未読のみ」フィルターが更新された場合検索を再実行する
  useValueChangeEffect(
    () => {
      // ViewModelを初期化
      resetAnnouncements()

      // 再検索を実行
      searchAnnouncements()
    },
    [resetAnnouncements, searchAnnouncements],
    unreadOnly,
    true // 初回ロード時には実行しない（スキップする）
  )

  // announcment表示イベントハンドラ
  const handleAnnouncementInView = useCallback(
    (inView: boolean, announcementId: string) => {
      if (inView) {
        announcementInView(announcementId)
      }
    },
    [announcementInView]
  )

  if (displayItems.length === 0) {
    return null
  }

  return (
    <div
      className="flex flex-col overflow-y-scroll"
      ref={itemsScrollableDivRef}
    >
      {displayItems.map(({ announcement, handleAdditionalLoading }) => (
        <AnnouncementListItem
          key={announcement.id}
          id={`announcement_${announcement.id}`}
          item={announcement}
          container={itemsScrollableDivRef}
          onAnnouncementInView={handleAnnouncementInView}
          selectedItemId={selectedAnnouncementId}
          setSelectedItemId={setSelectedAnnouncementId}
          onAdditionalLoading={handleAdditionalLoading}
        />
      ))}
    </div>
  )
})
